// Passive mouse behaviour tracker — detects readability struggle
// signals on text elements and asks the Accessibility Vuex module
// to bump the font scale when the score crosses a threshold.
//
// Signals collected (weighted):
//   • prolonged hover over a text element (> 1.5s without moving far)
//   • repeated scrolling within the same vertical region (user went
//     back and re-read)
//   • repeated text selection over overlapping ranges (user selects
//     the same sentence multiple times — classic struggle pattern)
//
// Each signal adds to a rolling score that decays over time. When
// the score exceeds the threshold AND enough time has passed since
// the last auto-bump, we dispatch autoBumpFontScale and reset.

import i18n from '@/i18n';

const HOVER_MIN_MS = 1500;
const HOVER_MAX_JITTER_PX = 12;
const SCROLL_REPEAT_WINDOW_MS = 8000;
const SCROLL_REPEAT_BAND_PX = 220;
const SELECTION_REPEAT_WINDOW_MS = 12000;
const SCORE_THRESHOLD = 5;
const SCORE_DECAY_PER_SEC = 0.5;
const MIN_INTERVAL_BETWEEN_BUMPS_MS = 45000;

const TEXT_TAG_REGEX =
  /^(p|span|li|h1|h2|h3|h4|h5|h6|a|td|th|label|blockquote|dd|dt|article|section|main)$/i;

let store = null;
let scoreState = { score: 0, lastUpdate: Date.now() };
let hoverState = {
  element: null,
  startX: 0,
  startY: 0,
  startTime: 0,
  timer: null
};
let scrollHistory = []; // [{ top, time }]
let selectionHistory = []; // [{ text, time }]
let lastBumpAt = 0;
let started = false;
let handlers = {};

function decayScore() {
  const now = Date.now();
  const secs = (now - scoreState.lastUpdate) / 1000;
  if (secs > 0) {
    scoreState.score = Math.max(
      0,
      scoreState.score - secs * SCORE_DECAY_PER_SEC
    );
    scoreState.lastUpdate = now;
  }
}

function addScore(points, reason) {
  decayScore();
  scoreState.score += points;
  scoreState.lastUpdate = Date.now();
  maybeBump(reason);
}

function maybeBump(reason) {
  const now = Date.now();
  if (now - lastBumpAt < MIN_INTERVAL_BETWEEN_BUMPS_MS) return;
  if (scoreState.score < SCORE_THRESHOLD) return;
  if (!store) return;
  const dispatched = store.dispatch(
    'Accessibility/autoBumpFontScale',
    i18n.t(`Services.FontSizeAutoAdjust.${reason}`)
  );
  // Either a promise or a sync boolean — both work with the if
  Promise.resolve(dispatched).then((ok) => {
    if (ok) {
      lastBumpAt = now;
      scoreState.score = 0;
      scrollHistory = [];
      selectionHistory = [];
    }
  });
}

function isTextElement(el) {
  if (!el || el.nodeType !== 1) return false;
  if (!TEXT_TAG_REGEX.test(el.tagName)) return false;
  // Skip if hidden or tiny
  const text = (el.innerText || el.textContent || '').trim();
  if (text.length < 20) return false;
  // Skip inputs / buttons / their children
  const interactive = el.closest(
    'button, input, textarea, select, [role="button"]'
  );
  if (interactive) return false;
  return true;
}

function onMouseOver(e) {
  if (!isTextElement(e.target)) {
    clearHover();
    return;
  }
  hoverState.element = e.target;
  hoverState.startX = e.clientX;
  hoverState.startY = e.clientY;
  hoverState.startTime = Date.now();
  if (hoverState.timer) clearTimeout(hoverState.timer);
  hoverState.timer = setTimeout(() => {
    addScore(1.5, 'prolongedReading');
  }, HOVER_MIN_MS);
}

function onMouseMove(e) {
  if (!hoverState.element) return;
  const dx = Math.abs(e.clientX - hoverState.startX);
  const dy = Math.abs(e.clientY - hoverState.startY);
  if (dx > HOVER_MAX_JITTER_PX || dy > HOVER_MAX_JITTER_PX) {
    clearHover();
  }
}

function onMouseOut() {
  clearHover();
}

function clearHover() {
  if (hoverState.timer) {
    clearTimeout(hoverState.timer);
    hoverState.timer = null;
  }
  hoverState.element = null;
}

function onScroll() {
  const top = window.scrollY || document.documentElement.scrollTop || 0;
  const now = Date.now();
  // Drop old entries
  scrollHistory = scrollHistory.filter(
    (s) => now - s.time < SCROLL_REPEAT_WINDOW_MS
  );
  // Count how many times we've been in this vertical band
  const bandVisits = scrollHistory.filter(
    (s) => Math.abs(s.top - top) < SCROLL_REPEAT_BAND_PX
  ).length;
  scrollHistory.push({ top, time: now });
  if (bandVisits >= 3) {
    // User keeps scrolling back to the same area
    scrollHistory = scrollHistory.filter(
      (s) => Math.abs(s.top - top) >= SCROLL_REPEAT_BAND_PX
    );
    addScore(2, 'scrolledBack');
  }
}

function onSelectionChange() {
  const sel = window.getSelection && window.getSelection();
  if (!sel) return;
  const text = sel.toString().trim();
  if (text.length < 6) return;
  const now = Date.now();
  selectionHistory = selectionHistory.filter(
    (s) => now - s.time < SELECTION_REPEAT_WINDOW_MS
  );
  const overlapping = selectionHistory.filter((s) => {
    // overlap = one string contains part of the other
    return s.text === text || s.text.includes(text) || text.includes(s.text);
  }).length;
  selectionHistory.push({ text, time: now });
  if (overlapping >= 1) {
    selectionHistory = [];
    addScore(2.5, 'reselectedText');
  }
}

// Visibility change pauses the tracker (no need to accrue score when
// the tab is backgrounded)
function onVisibilityChange() {
  if (document.hidden) {
    clearHover();
  }
}

export function startFontSizeAutoAdjust(vuexStore) {
  if (started) return;
  store = vuexStore;
  handlers.over = onMouseOver;
  handlers.move = onMouseMove;
  handlers.out = onMouseOut;
  handlers.scroll = onScroll;
  handlers.sel = onSelectionChange;
  handlers.vis = onVisibilityChange;
  document.addEventListener('mouseover', handlers.over, true);
  document.addEventListener('mousemove', handlers.move, true);
  document.addEventListener('mouseout', handlers.out, true);
  window.addEventListener('scroll', handlers.scroll, { passive: true });
  document.addEventListener('selectionchange', handlers.sel);
  document.addEventListener('visibilitychange', handlers.vis);
  started = true;
}

export function stopFontSizeAutoAdjust() {
  if (!started) return;
  document.removeEventListener('mouseover', handlers.over, true);
  document.removeEventListener('mousemove', handlers.move, true);
  document.removeEventListener('mouseout', handlers.out, true);
  window.removeEventListener('scroll', handlers.scroll);
  document.removeEventListener('selectionchange', handlers.sel);
  document.removeEventListener('visibilitychange', handlers.vis);
  clearHover();
  started = false;
}
